import React, {useEffect, useRef, useState} from "react"
import {Button, Stack, Typography} from "@mui/material"
import Grid from "@mui/material/Grid2";
import {InspectService} from "../../../interfaces"
import {useDispatch, useSelector} from "react-redux"
import {RootState} from "../../../Redux"
import api from "../../../Services/api"
import BackButton from "../Common/BackButton";
import {ServiceCardType} from "../Types";
import {useTranslation} from "react-i18next";
import ShowServices from "./ShowServices";
import ReservationRedux from '../../../Redux/reservationRedux'
import ReturnToStartScreenTimer from '../Common/ReturnToStartScreenTimer'

const { changeReservation, confirmReservationChanges, postCreateSpecial, getCheckIn } = api.create();

interface SelectServiceProps {
    isDriveIn: boolean
    campaignCode: string
    afterInspectionProductId?: number
    handlePayment: (paymentMethod: string | null) => void
    serviceCard: ServiceCardType | null
    setServiceCard: (serviceCard: ServiceCardType) => void
    setSelectedService: (service: InspectService) => void
    onBack: () => void
    isMissingService?: boolean
}

const SelectService: React.FC<SelectServiceProps> = ({
    isDriveIn,
    campaignCode,
    afterInspectionProductId,
    handlePayment,
    serviceCard,
    setServiceCard,
    setSelectedService,
    onBack,
    isMissingService = false,
}) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const [service, setService] = useState<InspectService|null>(null)
    const [serviceId, setServiceId] = useState<number>(
        afterInspectionProductId || serviceCard?.reservation?.tasks[0]?.basket?.items[0]?.productId
    )
    const [disabled, setDisabled] = useState<boolean>(serviceCard?.sale?.hasPendingPayment || false)
    const [error, setError] = useState<string | null>(null);


    const registerInfo = useSelector((state: RootState) => state.reservation.registerInfo)
    const chainId = useSelector((state: RootState) => state.ui.settings.chain.id)
    const stationId = useSelector((state: RootState) => state.ui.settings.station.id)
    const customerInfo = useSelector((state: RootState) => state.ui.customer)

    const getRunning = useRef(false);
    useEffect(() => {
        const getServiceCard = async () => {
            if (getRunning.current) return
            if (!serviceCard) return
            if (serviceCard.token === null) return

            getRunning.current = true

            const response = await getCheckIn(serviceCard.token)
            if (response.ok) {
                const card = response.data as ServiceCardType
                setDisabled(card?.sale?.hasPendingPayment || false)
            }

            getRunning.current = false
        }

        getServiceCard()
    }, [serviceCard]);

    const changesRunning = useRef(false);

    const doChangeReservation = async () => {
        if (changesRunning.current) return
        if (!serviceCard) return
        if (serviceCard.token === null) return
        if (service === null) return

        changesRunning.current = true

        const response = await changeReservation({
            token: serviceCard.token,
            modifications: {
                reservation: {
                    chainId: chainId,
                    stationId: stationId,
                    "tasks": [{
                        "vehicle": serviceCard.sale.tasks?.[0]?.vehicle,
                        "event": {},
                        "basket": {
                            "items": [
                                {
                                    "productId": service.id,
                                    "name": service.name
                                }
                            ]
                        }
                    }]
                }
            }
        })
        if (response.ok) {
            const confirmResponse = await confirmReservationChanges({
                token: serviceCard.token,
            })
            if (confirmResponse.ok) {
                setServiceCard(confirmResponse.data as ServiceCardType)
            }
        }

        changesRunning.current = false
    }

    const handlePaymentClick = async (registerInfo: any, paymentMethod: string | null) => {
        setError(null); // Reset error state

        try {
            if (isDriveIn && serviceCard?.token === undefined) {
                const response = await postCreateSpecial(
                    chainId,
                    stationId,
                    'driveIn',
                    {
                        customerToken: customerInfo?.token ?? null,
                        reservation: {
                            chainId: chainId,
                            stationId: stationId,
                            "tasks": [
                                {
                                    "vehicle": {
                                        "registrationNumber": registerInfo.registrationNumber,
                                        "vehicleClass": registerInfo.vehicleClass,
                                        "vehicleGroup": registerInfo.vehicleGroup,
                                    },
                                    "event": {},
                                    "basket": {
                                        "items": [
                                            {
                                                "productId": service.id,
                                                "name": service.name
                                            }
                                        ]
                                    }
                                }
                            ]
                        }
                    },
                )
                if (response.ok) {
                    setServiceCard(response.data as ServiceCardType)
                    console.log('Drive-in reservation created successfully');
                } else {
                    setError(t('selfService.reservationFailed'));
                    return;
                }
            } else if (serviceCard !== null) {
                await doChangeReservation()
            }

            handlePayment(paymentMethod);
        } catch {
            setError(t('selfService.reservationFailed'));
        }
    }

    const serviceSelected = (selectedService: InspectService) => {
        if (selectedService === undefined) {
            return
        }
        setService(selectedService)
        setServiceId(selectedService.id)
    }

    const handleGoBack = () => {
        setService(null)
        setServiceId(undefined)
        dispatch(ReservationRedux.resetServices())
        onBack()
    }

    const paymentButtons = () => {
        return (service && (customerInfo.isInvoicingEnabled || ((service.listPrice?.value ?? 0) + (service.additionalFee?.value ?? 0) === 0)) ?
            <>
                <Button
                    disabled={! serviceId}
                    variant="contained"
                    color="secondary"
                    onClick={() => handlePaymentClick(registerInfo, 'paymentNotRequired')}
                >
                    {t('selfService.signUp')}
                </Button>
            </>
            :
            <>
                <Button
                    disabled={! serviceId}
                    variant="contained"
                    color="secondary"
                    onClick={() => handlePaymentClick(registerInfo, 'payWithCard')}
                >
                    {t('selfService.payWithCard')}
                </Button>
                <Button
                    disabled={! serviceId}
                    variant="contained"
                    color="secondary"
                    onClick={() => handlePaymentClick(registerInfo, 'payOnline')}
                >
                    {t('selfService.payOnline')}
                </Button>
            </>
        )
    }

    if(error) {
        return (
            <Grid container wrap="nowrap" direction="column" justifyContent="space-between" alignItems="center" spacing={4} className={'pageMainContainer'}>
                <Grid/>
                <Grid className="pageMainHeader">
                    <Typography variant="h3">
                        {error}
                    </Typography>
                </Grid>
                <Grid className="footerButtons">
                    <ReturnToStartScreenTimer time={10}/>
                </Grid>
                <Grid/>
            </Grid>
        )
    }

    return (
        <Grid container wrap="nowrap" direction="column" justifyContent="space-between" alignItems="center" spacing={4} className={'pageMainContainer'}>
            <Grid className="pageMainHeader">
                <Typography variant="h3">
                    {t('selfService.selectService')}
                </Typography>
            </Grid>
            <Grid size="grow" alignContent="center" className="pageScrollContainer">
                <ShowServices
                    isDriveIn={isDriveIn}
                    service={service}
                    setService={serviceSelected}
                    campaignCode={campaignCode}
                    serviceId={serviceId}
                    disabled={disabled}
                />
            </Grid>
            <Grid className="footerButtons">
                <Grid container wrap="nowrap" spacing={4} justifyContent="space-between" alignItems="end">
                    <Grid>
                        <BackButton onBack={handleGoBack} />
                    </Grid>
                    <Grid>
                        <Stack direction="row" spacing={2}>
                             {isDriveIn === true && paymentButtons()}
                             {isDriveIn === false && <>
                                 {isMissingService === true && paymentButtons()}
                                 {isMissingService === false &&
                                     <Button
                                         variant="contained"
                                         color="secondary"
                                         onClick={() => setSelectedService(service)}
                                     >
                                         {t('selfService.selectTime')}
                                     </Button>
                                 }
                             </>}
                        </Stack>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default SelectService
